import './polyfills';
import React, { RefObject } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { MuiThemeProvider } from '@material-ui/core/styles';
import createGenerateClassName from '@material-ui/styles/createGenerateClassName';
import { filterTokensFromPerformance } from '@digisign3-ui/utils';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import './adobeFonts.css';
import './index.css';
import theme from './theme';
import configureStore from './store/index';
import Signer from './containers/Signer';
import { IEventEmitter } from './common/types';
import EventEmitter from './lib/EventEmitter';
import { API_URL, Events } from './common/constants';
import { StylesProvider } from '@material-ui/styles';
import axios from 'axios';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { pdfjs } from 'react-pdf';
import { Datacenter, datadogRum } from '@datadog/browser-rum';
import { DATADOG_ENV } from './common/constants';
import { decodeToken } from './lib/token';
import { initializeLD } from './resources/launchDarkly';
import DigiSignAdsProvider from './components/DigiSignAdsProvider';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/es5/build/pdf.worker.js`;

const generateClassName = createGenerateClassName({
  productionPrefix: 'ds',
  seed: 'digisign3',
});

interface ILibraryOptions {
  envelopeId: string;
  signerId: string;
  apiKey: string;
  accessToken: string;
  elementId?: string;
  element?: Element;
  elementRef?: RefObject<HTMLElement>;
  showSuccessScreen?: boolean;
}

interface IDocumentOptions {
  apiKey: string;
  accessToken: string;
  envelopeId: string;
  documentId: string;
}

interface ICheckEnvelopeOptions {
  apiKey: string;
  accessToken: string;
  envelopeId: string;
  signerId: string;
}

export let renderTarget: Element;
async function init(options: ILibraryOptions) {
  const { envelopeId, signerId, showSuccessScreen = true } = options;
  const store = configureStore();

  if (DATADOG_ENV) {
    Sentry.init({
      dsn: 'https://80d4e4d6635141d9a3add163ae33da4c@o460680.ingest.sentry.io/5600798',
      autoSessionTracking: true,
      integrations: [new Integrations.BrowserTracing()],
      environment: DATADOG_ENV,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 0.1,
      beforeSend(event) {
        if (event.request?.url) {
          event.request.url = event.request.url.split('#')[0];
        }
        return event;
      },
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
      ],
      denyUrls: [
        'inhere', // integrator
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ],
    });

    filterTokensFromPerformance();
    datadogRum.init({
      applicationId: 'bcab28a5-22c0-438c-9a94-ba6ae428b205',
      clientToken: 'pub8855a524c6d3005642147116eef6d1bd',
      datacenter: Datacenter.US,
      sampleRate: 100,
      trackInteractions: true,
      env: DATADOG_ENV,
    });
  }

  if (options.element) {
    renderTarget = options.element;
  } else if (options.elementId) {
    renderTarget = document.getElementById(options.elementId)!;
  } else if (options.elementRef) {
    renderTarget = options.elementRef.current!;
  } else {
    throw 'DigiSign: Missing one of: `elementId`, `element`, or `elementRef`';
  }

  const props = {
    signerId,
    envelopeId,
    showSuccessScreen,
  };

  await initializeLD();

  ReactDOM.render(
    <Provider store={store}>
      <StylesProvider generateClassName={generateClassName}>
        <MuiThemeProvider theme={theme}>
          <DigiSignAdsProvider>
            <Signer {...props} />
          </DigiSignAdsProvider>
        </MuiThemeProvider>
      </StylesProvider>
    </Provider>,
    renderTarget
  );
}

const validateMountOptions = (options: ILibraryOptions): boolean => {
  const { envelopeId, signerId, apiKey, accessToken } = options;
  if (
    typeof envelopeId === 'string' &&
    typeof signerId === 'string' &&
    typeof apiKey === 'string' &&
    typeof accessToken === 'string'
  ) {
    return true;
  }
  return false;
};

let eventEmitter: IEventEmitter = new EventEmitter();
let apiKey: string;
let accessToken: string;

const lib = {
  mount: (options: ILibraryOptions) => {
    try {
      const { EnvelopeId: envelopeId = options.envelopeId, SignerId: signerId = options.signerId } = JSON.parse(
        atob(options.accessToken.split('.')[1])
      );
      options.envelopeId = envelopeId;
      options.signerId = signerId;

      if (!validateMountOptions(options)) {
        throw new Error('Oho! The options you provided are not valid!');
      }

      apiKey = options.apiKey;
      accessToken = options.accessToken;
      decodeToken(accessToken);
      init(options);

      return eventEmitter;
    } catch (error) {
      throw new Error('The accessToken provided is invalid!');
    }
  },
  unmount: (element: HTMLElement) => {
    try {
      ReactDOM.unmountComponentAtNode(element);
    } catch (error) {
      eventEmitter.emit(Events.ERROR, 'Please provide a valid HTMLElement to unmount');
    }
  },
  getPresignedDocumentUrl: async (options: IDocumentOptions) => {
    const { apiKey, accessToken, envelopeId, documentId } = options;
    try {
      const response = await axios.get(`${API_URL}/envelopes/${envelopeId}/documents/${documentId}/downloadURL`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-api-key': apiKey,
        },
      });
      return response.data.result.preSignedDownloadUrl;
    } catch (error) {
      throw error.response.data;
    }
  },
  isEnvelopeReadyForSigning: async (options: ICheckEnvelopeOptions) => {
    const { apiKey, accessToken, envelopeId, signerId } = options;
    try {
      const response = await axios.get(`${API_URL}/envelopes/${envelopeId}/signer/${signerId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-api-key': apiKey,
        },
      });
      return response.data;
    } catch (error) {
      return error.response.data;
    }
  },
};

export { lib, eventEmitter, apiKey, accessToken };

if (process.env.NODE_ENV === 'development') {
  // @ts-ignore
  window.DigiSign = lib;
}
